import type { BoardData } from '../model/schema.js';
import { splitParentKey } from '../model/layerSplit.js';
import type { CommentAnchor, OpenItemTopic } from '../api/dto.js';
import { OPEN_ITEM_TOPICS } from '../api/dto.js';

type RowTable = Extract<CommentAnchor, { type: 'row' }>['table'];

/** Table rows belong to the area the list describes. */
const ROW_TOPIC: Record<RowTable, OpenItemTopic> = {
  input: 'microphones',
  patch: 'stageboxes',
  monitors: 'monitors',
  mixes: 'monitors',
  sends: 'monitors',
  power: 'power',
  'power-sources': 'power',
  'power-loads': 'power',
  network: 'network',
  stands: 'microphones',
  'stand-summary': 'microphones',
  openitems: 'general',
};

export function isOpenItemTopic(v: unknown): v is OpenItemTopic {
  return (OPEN_ITEM_TOPICS as readonly unknown[]).includes(v);
}

/** Topic of a layer: its built-in key when that is a topic (notes, ink, custom layers → general). */
export function topicForLayer(data: BoardData, layerId: string | undefined): OpenItemTopic {
  const key = layerId === undefined ? undefined : data.layers[layerId]?.key;
  const topic = key === undefined ? undefined : splitParentKey(key);
  return isOpenItemTopic(topic) ? topic : 'general';
}

/**
 * Default topic of an open item (decision O5): derived from the layer of the linked object or
 * the table of the linked row; points, the whole board and unlinked items are `general`.
 */
export function topicForAnchor(
  data: BoardData,
  anchor: CommentAnchor | null | undefined,
): OpenItemTopic {
  if (!anchor) return 'general';
  if (anchor.type === 'row') return ROW_TOPIC[anchor.table];
  if (anchor.type !== 'item') return 'general';
  const item = data.items[anchor.itemId];
  return topicForLayer(data, item ? item.layerId : data.freeform[anchor.itemId]?.layerId);
}
